import { fetchLandingDomains } from '@/api/landings';
import { ROUTES } from '@/app/routes';
import Pressable from '@/components/motion/pressable';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingDomain } from '@/entities/landing.entity';
import { PageScroll } from '@/layouts/page-layout';
import AddLandingDomainModal from '@/pages/landings/components/add-landing-domain-modal';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import LandingsPageCard from '@/pages/landings/components/landings-page-card';
import LandingsGate from '@/pages/landings/landings-gate';
import LandingDomainStatusBadge from '@/pages/landings/landing-domain-status-badge';
import { ChevronRightIcon, GlobeIcon, LayoutTemplateIcon, LinkIcon, PlusIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

function formatDomainsCount(count: number): string {
	if (count === 0) return 'Нет доменов';
	const word = count === 1 ? 'домен' : count < 5 ? 'домена' : 'доменов';
	return `${count} ${word}`;
}

function formatDomainSitesCount(count: number): string {
	const word = count === 1 ? 'сайт' : count < 5 ? 'сайта' : 'сайтов';
	return `${count} ${word}`;
}

function formatDomainLinksCount(count: number): string {
	const word = count === 1 ? 'ссылка' : count < 5 ? 'ссылки' : 'ссылок';
	return `${count} ${word}`;
}

export default function LandingsDomainsPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<DomainsContent />
			</LandingsGate>
		</PageScroll>
	);
}

function DomainsContent() {
	const navigate = useNavigate();
	const [domains, setDomains] = useState<ILandingDomain[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [addOpen, setAddOpen] = useState(false);

	const loadDomains = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchLandingDomains();
			setDomains(data);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadDomains(true);
	}, [loadDomains]);

	return (
		<LandingsPageCard
			icon={GlobeIcon}
			title='Домены лендингов'
			description={formatDomainsCount(domains.length)}
			color='chart-1'
			action={
				<Button type='button' size='sm' onClick={() => setAddOpen(true)}>
					<PlusIcon size={16} className='mr-1' />
					Добавить
				</Button>
			}
		>
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : error ? (
				<LandingsErrorState onRetry={() => void loadDomains(true)} />
			) : domains.length === 0 ? (
				<LandingsEmptyState
					icon={GlobeIcon}
					title='Доменов пока нет'
					description='Добавьте домен и настройте NS-записи у регистратора.'
					actionLabel='Добавить домен'
					onAction={() => setAddOpen(true)}
				/>
			) : (
				<div className='flex flex-col gap-3'>
					{domains.map(domain => (
						<Pressable
							key={domain.id}
							role='button'
							tabIndex={0}
							className='rounded-xl border border-border bg-card p-3 shadow-sm text-left active:bg-accent/30 cursor-pointer'
							onClick={() => navigate(`${ROUTES.landingsDomain}${domain.id}`)}
						>
							<div className='flex items-start justify-between gap-2'>
								<div className='min-w-0 flex-1'>
									<div className='flex items-center gap-2'>
										<GlobeIcon size={16} className='shrink-0 text-muted-foreground' />
										<p className='truncate font-medium'>{domain.zoneName}</p>
									</div>
									{domain.errorMessage ? (
										<p className='mt-1 text-xs text-destructive line-clamp-2'>{domain.errorMessage}</p>
									) : null}
								</div>
								<LandingDomainStatusBadge status={domain.status} />
							</div>
							<div className='mt-3 flex items-center justify-between gap-2 border-t border-border/60 pt-3'>
								<div className='flex items-center gap-3 text-xs text-muted-foreground'>
									<span className='flex items-center gap-1.5'>
										<LayoutTemplateIcon size={12} />
										{formatDomainSitesCount(domain.sitesCount ?? 0)}
									</span>
									<span className='flex items-center gap-1.5'>
										<LinkIcon size={12} />
										{formatDomainLinksCount(domain.linksCount ?? 0)}
									</span>
								</div>
								<ChevronRightIcon size={16} className='text-muted-foreground' />
							</div>
						</Pressable>
					))}
				</div>
			)}

			<AddLandingDomainModal open={addOpen} onOpenChange={setAddOpen} onCreated={() => void loadDomains()} />
		</LandingsPageCard>
	);
}
